import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { User, Save, Edit3, Lock, Eye, EyeOff } from 'lucide-react';

const ProfileView = () => {
  const { user, updateUserProfile, changePassword } = useAuth();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    displayName: '',
    crewName: '',
    email: ''
  });
  
  // Password change state
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Ajout d'un hook pour détecter le thème
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return document.body.classList.contains('light') ? 'light' : 'dark';
    }
    return 'dark';
  });
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setTheme(document.body.classList.contains('light') ? 'light' : 'dark');
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (user) {
      setProfileData({
        displayName: user.profile?.displayName || user.username || '',
        crewName: user.profile?.crewName || '',
        email: user.email || ''
      });
    }
  }, [user]);

  const handleSave = async () => {
    const res = await updateUserProfile(profileData);
    setIsEditing(false);
    if (res?.success) {
      if (res.emailError) {
        toast({
          title: "Profil mis à jour (email inchangé)",
          description: res.emailError,
          variant: "destructive"
        });
      } else {
        toast({
          title: "Profile Updated! ✅",
          description: "Your profile has been updated successfully."
        });
      }
    } else {
      toast({
        title: "Échec de la mise à jour",
        description: res?.error || "Veuillez réessayer.",
        variant: "destructive"
      });
    }
  };

  const handleCancel = () => {
    setProfileData({
      displayName: user.profile?.displayName || user.username || '',
      crewName: user.profile?.crewName || '',
      email: user.email || ''
    });
    setIsEditing(false);
  };

  const handlePasswordChange = async () => {
    // Validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs.",
        variant: "destructive"
      });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: "Erreur",
        description: "Les mots de passe ne correspondent pas.",
        variant: "destructive"
      });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast({
        title: "Erreur",
        description: "Le nouveau mot de passe doit contenir au moins 6 caractères.",
        variant: "destructive"
      });
      return;
    }

    const result = await changePassword(passwordData.currentPassword, passwordData.newPassword);
    
    if (result.success) {
      toast({
        title: "Succès",
        description: result.message
      });
      setIsChangingPassword(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } else {
      toast({
        title: "Erreur",
        description: result.error,
        variant: "destructive"
      });
    }
  };

  const handlePasswordCancel = () => {
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setIsChangingPassword(false);
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className={`w-full max-w-md border-2 bg-white text-black`}>
          <CardContent className="p-6 text-center">
            <User className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <h2 className="text-xl font-bold mb-2">Not Logged In</h2>
            <p className="text-gray-500">Please log in to view your profile.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <Card className={`border-2 bg-white text-black`}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-2xl font-bold flex items-center gap-2">
                <User className="w-6 h-6" />
                Profile Settings
              </CardTitle>
              {!isEditing && (
                <Button
                  onClick={() => setIsEditing(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Edit3 className="w-4 h-4 mr-2" />
                  Edit Profile
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="displayName">Display Name</Label>
                <Input
                  id="displayName"
                  value={profileData.displayName}
                  onChange={(e) => setProfileData(prev => ({ ...prev, displayName: e.target.value }))}
                  disabled={!isEditing}
                  className={`bg-white text-black border-black focus:ring-2 focus:ring-blue-500 transition-all duration-200`}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="crewName">Crew Name (Optional)</Label>
                <Input
                  id="crewName"
                  value={profileData.crewName}
                  onChange={(e) => setProfileData(prev => ({ ...prev, crewName: e.target.value }))}
                  disabled={!isEditing}
                  className={`bg-white text-black border-black focus:ring-2 focus:ring-blue-500 transition-all duration-200`}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                  disabled={!isEditing}
                  className={`bg-white text-black border-black focus:ring-2 focus:ring-blue-500 transition-all duration-200`}
                />
              </div>

              <div className="space-y-2">
                <Label>Username</Label>
                <Input
                  value={user.username}
                  disabled
                  className={`${theme === 'light' ? 'bg-gray-100 text-gray-500 border-gray-300' : 'bg-gray-800 text-gray-400 border-gray-600'}`}
                />
              </div>
            </div>

            {isEditing && (
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleSave}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
                <Button
                  onClick={handleCancel}
                  variant="outline"
                  className={`${theme === 'light' ? 'border-black text-black hover:bg-gray-100' : 'border-white text-white hover:bg-gray-800'}`}
                >
                  Cancel
                </Button>
              </div>
            )}

            <div className="border-t pt-6 mt-6">
              <h3 className="text-lg font-semibold mb-4">Account Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Member since:</span>
                  <p className="text-gray-500">{user.profile?.joinedDate || 'Unknown'}</p>
                </div>
                {/*
                <div>
                  <span className="font-medium">Account type:</span>
                  <p className="text-gray-500">Free</p>
                </div>*/}
              </div>
            </div>

            <div className="border-t pt-6 mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  Security
                </h3>
                {!isChangingPassword && (
                  <Button
                    onClick={() => setIsChangingPassword(true)}
                    variant="outline"
                    className={`${theme === 'light' ? 'border-black text-black hover:bg-gray-100' : 'border-white text-white hover:bg-gray-800'}`}
                  >
                    Change Password
                  </Button>
                )}
              </div>

              {isChangingPassword && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">Current Password</Label>
                    <div className="relative">
                      <Input
                        id="currentPassword"
                        type={showPasswords.current ? "text" : "password"}
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                        className="bg-white text-black border-black focus:ring-2 focus:ring-blue-500 pr-10"
                        placeholder="Enter your current password"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                      >
                        {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        type={showPasswords.new ? "text" : "password"}
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                        className="bg-white text-black border-black focus:ring-2 focus:ring-blue-500 pr-10"
                        placeholder="Enter your new password (min. 6 characters)"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                      >
                        {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showPasswords.confirm ? "text" : "password"}
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        className="bg-white text-black border-black focus:ring-2 focus:ring-blue-500 pr-10"
                        placeholder="Confirm your new password"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                      >
                        {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button
                      onClick={handlePasswordChange}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Change Password
                    </Button>
                    <Button
                      onClick={handlePasswordCancel}
                      variant="outline"
                      className={`${theme === 'light' ? 'border-black text-black hover:bg-gray-100' : 'border-white text-white hover:bg-gray-800'}`}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {!isChangingPassword && (
                <div className="text-sm text-gray-500">
                  <p>Last password change: Never (or unknown)</p>
                  <p className="mt-1">We recommend changing your password regularly for better security.</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProfileView; 